import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Menu,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconChevronDown,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { listPaymentTerms } from '../paymentTerms/api';
import {
  deletePurchaseOrder,
  deletePurchaseOrderLine,
  getPurchaseOrder,
  listPurchaseOrderLines,
  listShipTos,
  setPurchaseOrderStatus,
  shipToSummary,
  updatePurchaseOrder,
  type PurchaseOrderAction,
  type PurchaseOrderLineDto,
} from './api';
import { PurchaseOrderLineModal } from './PurchaseOrderLineModal';
import { PurchaseOrderStatusBadge } from './PurchaseOrderStatusBadge';

/**
 * One purchase order: its header, its lines, and the four things a person can decide about it.
 *
 * A route rather than a modal, which is a departure from the list-plus-dialog shape everything
 * else in the app uses. A purchase order is a document worked on over days, with a line grid that
 * needs its own editor — a dialog inside a dialog — and a URL somebody can return to or send to a
 * colleague. Receiving (M4.3) will want the same shape.
 *
 * WHAT IS NOT EDITABLE, AND WHY
 * The PO number, the warehouse and the supplier have no fields. The number identifies the order to
 * the supplier; the other two decide whose goods arrive where, and changing either on an order
 * that exists is a different order wearing the same number. The totals have no fields either:
 * they are derived from the lines on every write, so a box to type one in would be a box to make
 * the header disagree with what is under it.
 */
export function PurchaseOrderDetailRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const { id } = useParams({ strict: false }) as { id: string };

  const [lineModalOpen, { open: openLineModal, close: closeLineModal }] = useDisclosure(false);
  const [editingLine, setEditingLine] = useState<PurchaseOrderLineDto | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canWrite = permissions.has('po:write');

  const order = useQuery({
    queryKey: ['purchase-order', id] as const,
    queryFn: ({ signal }) => getPurchaseOrder(id, signal),
  });

  const lines = useQuery({
    queryKey: ['purchase-order-lines', id] as const,
    queryFn: ({ signal }) => listPurchaseOrderLines(id, signal),
  });

  const shipTos = useQuery({
    queryKey: ['ship-tos'] as const,
    queryFn: ({ signal }) => listShipTos(false, signal),
  });

  const terms = useQuery({
    queryKey: ['payment-terms'] as const,
    queryFn: ({ signal }) => listPaymentTerms(false, signal),
  });

  // ---- The editable header, held locally until Save ------------------------
  const [expected, setExpected] = useState('');
  const [shipToId, setShipToId] = useState<string | null>(null);
  const [paymentTermId, setPaymentTermId] = useState<string | null>(null);
  const [attentionTo, setAttentionTo] = useState('');
  const [placedBy, setPlacedBy] = useState('');
  const [externalPoNumber, setExternalPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [taxAmount, setTaxAmount] = useState<number | ''>('');

  const po = order.data;

  useEffect(() => {
    if (!po) return;
    setExpected(po.expectedReceiptDateUtc ? po.expectedReceiptDateUtc.slice(0, 10) : '');
    setShipToId(po.shipToId);
    setPaymentTermId(po.paymentTermId);
    setAttentionTo(po.attentionTo ?? '');
    setPlacedBy(po.placedBy ?? '');
    setExternalPoNumber(po.externalPoNumber ?? '');
    setNotes(po.notes ?? '');
    setTaxAmount(po.taxAmount);
  }, [po]);

  /** Closed and cancelled are final: the server refuses every edit (53726). */
  const isFinal = po ? po.status === 'closed' || po.status === 'cancelled' : false;
  const editable = canWrite && !isFinal;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-order-lines', id] });
    void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
  }

  function describe(err: unknown, fallback: string): string {
    if (!(err instanceof ApiError)) return t(fallback);
    // Every refusal here names what blocked it — the order number, how much has arrived, what to
    // do instead. Restating them generically would drop the only actionable part.
    return err.problem.detail ?? err.problem.title ?? t(fallback);
  }

  const saveHeader = useMutation({
    mutationFn: () =>
      updatePurchaseOrder(id, {
        expectedReceiptDateUtc: expected ? `${expected}T00:00:00Z` : null,
        shipToId,
        paymentTermId,
        attentionTo: attentionTo.trim() || null,
        placedBy: placedBy.trim() || null,
        externalPoNumber: externalPoNumber.trim() || null,
        notes: notes.trim() || null,
        taxAmount: taxAmount === '' ? null : Number(taxAmount),
      }),
    onSuccess: () => {
      notifications.show({ message: t('purchaseOrders.saved'), color: 'green' });
      setActionError(null);
      refresh();
    },
    onError: (err) => setActionError(describe(err, 'purchaseOrders.errors.saveFailed')),
  });

  const changeStatus = useMutation({
    mutationFn: (action: PurchaseOrderAction) => setPurchaseOrderStatus(id, action),
    onSuccess: (updated) => {
      notifications.show({
        message: t(`purchaseOrders.statusChanged.${updated.status}`, {
          defaultValue: t('purchaseOrders.saved'),
        }),
        color: 'green',
      });
      setActionError(null);
      refresh();
    },
    onError: (err) => setActionError(describe(err, 'purchaseOrders.errors.statusFailed')),
  });

  const removeOrder = useMutation({
    mutationFn: () => deletePurchaseOrder(id),
    onSuccess: () => {
      notifications.show({ message: t('purchaseOrders.deleted'), color: 'green' });
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      void navigate({ to: '/app/purchase-orders' });
    },
    onError: (err) => setActionError(describe(err, 'purchaseOrders.errors.deleteFailed')),
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) => deletePurchaseOrderLine(id, lineId),
    onSuccess: () => {
      setActionError(null);
      refresh();
    },
    onError: (err) => setActionError(describe(err, 'purchaseOrders.errors.lineFailed')),
  });

  const money = useMemo(() => {
    const currency = po?.currencyCode ?? 'USD';
    return (value: number) =>
      new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  }, [po?.currencyCode]);

  /**
   * The item is the headline of a line card, not the line number: on a phone the number is a
   * reference you quote to the supplier, and the item is what you are looking at. Ordered against
   * received stays a pair of labelled rows, because the gap between them is the whole point.
   */
  const lineColumns: DataColumn<PurchaseOrderLineDto>[] = useMemo(
    () => [
      {
        key: 'item',
        label: t('purchaseOrders.item'),
        card: 'title',
        render: (l) => (
          <>
            <Text size="sm">{l.itemNumber}</Text>
            <Text size="xs" c="dimmed">
              {l.itemDescription}
            </Text>
          </>
        ),
      },
      {
        key: 'lineNumber',
        label: t('purchaseOrders.lineNumber'),
        width: 60,
        card: 'aside',
        render: (l) => l.lineNumber,
      },
      {
        key: 'ordered',
        label: t('purchaseOrders.ordered'),
        align: 'right',
        render: (l) => l.quantityOrdered,
      },
      {
        key: 'received',
        label: t('purchaseOrders.received'),
        align: 'right',
        render: (l) => l.quantityReceived,
      },
      { key: 'unit', label: t('purchaseOrders.unit'), render: (l) => l.uomCode },
      {
        key: 'unitPrice',
        label: t('purchaseOrders.unitPrice'),
        align: 'right',
        render: (l) => money(l.unitPrice),
      },
      {
        key: 'lineTotal',
        label: t('purchaseOrders.lineTotal'),
        align: 'right',
        render: (l) => money(l.lineTotal),
      },
    ],
    [t, money],
  );

  if (order.isLoading) {
    return (
      <>
        <PageHeader title={t('nav.purchaseOrders')} back />
        <Center py="xl">
          <Loader color="invetrak" />
        </Center>
      </>
    );
  }

  if (order.isError || !po) {
    return (
      <>
        <PageHeader title={t('nav.purchaseOrders')} back />
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {t('purchaseOrders.errors.notFound')}
        </Alert>
      </>
    );
  }


  /**
   * Which of the four acts apply right now.
   *
   * Offering all of them and letting the server refuse would be honest but unkind: the operator
   * learns the rule by being told no. These mirror usp_PurchaseOrder_SetStatus exactly.
   */
  const canIssue = po.status === 'open' && po.lineCount > 0;
  const canCancel = !isFinal && !po.hasReceipts;
  const canClose = po.status !== 'cancelled' && !isFinal;
  const canReopen = isFinal;
  const canDelete = canWrite && !po.hasReceipts;

  return (
    <>
      <PageHeader
        title={po.poNumber}
        back
        subtitle={
          <Group gap="xs">
            <PurchaseOrderStatusBadge status={po.status} />
            <Text size="sm" c="dimmed">
              {t('purchaseOrders.headline', {
                supplier: po.supplierName,
                warehouse: po.warehouseCode,
              })}
            </Text>
          </Group>
        }
        actions={
          canWrite && (
            <Group gap="sm">
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button variant="default" rightSection={<IconChevronDown size={16} />}>
                    {t('purchaseOrders.actions')}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item disabled={!canIssue} onClick={() => changeStatus.mutate('issue')}>
                    {t('purchaseOrders.issue')}
                  </Menu.Item>
                  <Menu.Item disabled={!canClose} onClick={() => changeStatus.mutate('close')}>
                    {t('purchaseOrders.close')}
                  </Menu.Item>
                  <Menu.Item disabled={!canCancel} onClick={() => changeStatus.mutate('cancel')}>
                    {t('purchaseOrders.cancel')}
                  </Menu.Item>
                  <Menu.Item disabled={!canReopen} onClick={() => changeStatus.mutate('reopen')}>
                    {t('purchaseOrders.reopen')}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    disabled={!canDelete}
                    onClick={() => removeOrder.mutate()}
                  >
                    {t('purchaseOrders.delete')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <Button
                color="invetrak"
                onClick={() => saveHeader.mutate()}
                loading={saveHeader.isPending}
                disabled={!editable}
              >
                {t('shell.saveChanges')}
              </Button>
            </Group>
          )
        }
      />

      <Stack gap="md">
        {isFinal && (
          <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
            {t('purchaseOrders.finalNotice', { status: t(`purchaseOrders.statuses.${po.status}`) })}
          </Alert>
        )}

        {actionError && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            {actionError}
          </Alert>
        )}

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>{t('purchaseOrders.orderSection')}</Title>

              {/* Fixed for the life of the order — shown, not offered. */}
              <SimpleGrid cols={2} spacing="xs">
                <Text size="sm" c="dimmed">{t('purchaseOrders.supplier')}</Text>
                <Text size="sm">{po.supplierNumber} — {po.supplierName}</Text>
                <Text size="sm" c="dimmed">{t('purchaseOrders.warehouse')}</Text>
                <Text size="sm">{po.warehouseCode}</Text>
                <Text size="sm" c="dimmed">{t('purchaseOrders.orderDate')}</Text>
                <Text size="sm">{new Date(po.orderDateUtc).toLocaleDateString()}</Text>
              </SimpleGrid>

              <Divider />

              <TextInput
                type="date"
                label={t('purchaseOrders.expected')}
                value={expected}
                onChange={(e) => setExpected(e.currentTarget.value)}
                disabled={!editable}
              />
              <Select
                label={t('purchaseOrders.paymentTerm')}
                placeholder={t('purchaseOrders.paymentTermPlaceholder')}
                data={(terms.data ?? []).map((p) => ({
                  value: p.paymentTermId,
                  label: `${p.paymentTermCode} — ${p.displayName}`,
                }))}
                value={paymentTermId}
                onChange={setPaymentTermId}
                disabled={!editable || terms.isLoading}
                searchable
                clearable
              />
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <TextInput
                  label={t('purchaseOrders.placedBy')}
                  value={placedBy}
                  onChange={(e) => setPlacedBy(e.currentTarget.value)}
                  maxLength={100}
                  disabled={!editable}
                />
                <TextInput
                  label={t('purchaseOrders.externalPoNumber')}
                  value={externalPoNumber}
                  onChange={(e) => setExternalPoNumber(e.currentTarget.value)}
                  maxLength={50}
                  disabled={!editable}
                />
              </SimpleGrid>
              <Textarea
                label={t('purchaseOrders.notes')}
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
                autosize
                minRows={2}
                maxRows={5}
                disabled={!editable}
              />
            </Stack>
          </Card>

          <Card withBorder>
            <Stack gap="md">
              <Title order={4}>{t('purchaseOrders.deliverySection')}</Title>

              <Select
                label={t('purchaseOrders.shipTo')}
                description={t('purchaseOrders.shipToRepick')}
                placeholder={t('purchaseOrders.shipToPlaceholder')}
                data={(shipTos.data ?? []).map((s) => ({
                  value: s.purchaseOrderShipToId,
                  label: shipToSummary(s),
                }))}
                value={shipToId}
                onChange={setShipToId}
                disabled={!editable || shipTos.isLoading}
                searchable
                clearable
              />

              {/* The address AS SENT, not a live read of the book. If somebody corrects the book
                  entry later this does not move, which is the point of storing both. */}
              {po.shipToName ? (
                <Stack gap={2}>
                  <Text size="sm" fw={500}>{po.shipToName}</Text>
                  {po.shipToAddressLine1 && <Text size="sm">{po.shipToAddressLine1}</Text>}
                  {po.shipToAddressLine2 && <Text size="sm">{po.shipToAddressLine2}</Text>}
                  <Text size="sm">
                    {[po.shipToCity, po.shipToStateProvince, po.shipToPostalCode]
                      .filter(Boolean)
                      .join(', ')}
                  </Text>
                  {po.shipToPhone && <Text size="sm" c="dimmed">{po.shipToPhone}</Text>}
                  <Text size="xs" c="dimmed" mt={4}>
                    {t('purchaseOrders.shipToSnapshot')}
                  </Text>
                </Stack>
              ) : (
                <Text size="sm" c="dimmed">{t('purchaseOrders.noShipTo')}</Text>
              )}

              <TextInput
                label={t('purchaseOrders.attentionTo')}
                value={attentionTo}
                onChange={(e) => setAttentionTo(e.currentTarget.value)}
                maxLength={150}
                disabled={!editable}
              />

              <Divider />

              <SimpleGrid cols={2} spacing="xs">
                <Text size="sm" c="dimmed">{t('purchaseOrders.subtotal')}</Text>
                <Text size="sm" ta="right">{money(po.subtotalAmount)}</Text>
              </SimpleGrid>
              <NumberInput
                label={t('purchaseOrders.tax')}
                description={t('purchaseOrders.taxHint')}
                value={taxAmount}
                onChange={(v) => setTaxAmount(v === '' ? '' : Number(v))}
                min={0}
                decimalScale={4}
                disabled={!editable}
              />
              <SimpleGrid cols={2} spacing="xs">
                <Text fw={600}>{t('purchaseOrders.total')}</Text>
                <Text fw={600} ta="right">{money(po.totalAmount)}</Text>
              </SimpleGrid>
              <Text size="xs" c="dimmed">{t('purchaseOrders.totalsDerived')}</Text>
            </Stack>
          </Card>
        </SimpleGrid>

        <Card withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>{t('purchaseOrders.linesSection')}</Title>
            {editable && (
              <Button
                size="sm"
                color="invetrak"
                leftSection={<IconPlus size={16} />}
                onClick={() => {
                  setEditingLine(null);
                  openLineModal();
                }}
              >
                {t('purchaseOrders.addLine')}
              </Button>
            )}
          </Group>

          {lines.isLoading ? (
            <Center py="lg">
              <Loader color="invetrak" size="sm" />
            </Center>
          ) : (lines.data ?? []).length === 0 ? (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('purchaseOrders.noLinesYet')}
            </Alert>
          ) : (
            <ResponsiveTable
              rows={lines.data ?? []}
              columns={lineColumns}
              rowKey={(l) => l.purchaseOrderLineId}
              minWidth={800}
              striped
              actions={
                editable
                  ? (l) => (
                      <>
                        <Tooltip label={t('purchaseOrders.editLineAria', { number: l.lineNumber })}>
                          <ActionIcon
                            variant="subtle"
                            aria-label={t('purchaseOrders.editLineAria', { number: l.lineNumber })}
                            onClick={() => {
                              setEditingLine(l);
                              openLineModal();
                            }}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {/* A line with receipts cannot go: the stock that arrived would have no
                            explanation. The server refuses it (53746) either way. */}
                        <Tooltip
                          label={
                            l.quantityReceived > 0
                              ? t('purchaseOrders.lineHasReceipts')
                              : t('purchaseOrders.deleteLineAria', { number: l.lineNumber })
                          }
                        >
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={t('purchaseOrders.deleteLineAria', { number: l.lineNumber })}
                            disabled={l.quantityReceived > 0}
                            onClick={() => removeLine.mutate(l.purchaseOrderLineId)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )
                  : undefined
              }
            />
          )}
        </Card>
      </Stack>

      <PurchaseOrderLineModal
        purchaseOrderId={id}
        line={editingLine}
        opened={lineModalOpen}
        onClose={closeLineModal}
        onSaved={() => refresh()}
      />
    </>
  );
}
